import type { HostCapabilities } from "../../capabilities";
import { warn } from "../infra/effects";
import type { MarlinStoreDb } from "../infra/db";
import type { ScreenDirection } from "../types";
import * as ops from "./store";

const windowApi = (
  db: MarlinStoreDb,
  screenId: number,
  capabilities: Pick<HostCapabilities, "screens" | "windows">,
) => {
  const focusAfter = async (result: Promise<ops.SelectionResult>) => {
    const { focusScreenId } = await result;
    if (focusScreenId !== null)
      await capabilities.windows?.focus(focusScreenId).catch(warn("focus"));
  };
  const arrangement = async () =>
    capabilities.screens.getScreenArrangement(
      await ops.getVisibleScreenIds(db),
    );
  return {
    getSelected: () => ops.getSelected(db),
    getSelectedCamera: () => ops.getSelectedCamera(db, screenId),
    selectCamera: (
      cameraId: Parameters<typeof ops.selectCamera>[2],
      machineId: Parameters<typeof ops.selectCamera>[3],
    ) => focusAfter(ops.selectCamera(db, screenId, cameraId, machineId)),
    selectNextCamera: () => focusAfter(ops.selectNextCamera(db, screenId)),
    selectPreviousCamera: () =>
      focusAfter(ops.selectPreviousCamera(db, screenId)),
    selectNextScreen: () =>
      focusAfter(arrangement().then((a) => ops.selectNextScreen(db, a))),
    selectPreviousScreen: () =>
      focusAfter(arrangement().then((a) => ops.selectPreviousScreen(db, a))),
    selectScreenInDirection: (direction: ScreenDirection) =>
      focusAfter(
        arrangement().then((a) =>
          ops.selectScreenInDirection(db, screenId, direction, a),
        ),
      ),
  };
};

export const createSelectionApi = (
  db: MarlinStoreDb,
  screenId: number,
  capabilities: Pick<HostCapabilities, "screens" | "windows">,
) => ({
  ...windowApi(db, screenId, capabilities),
  forScreen: (id: number) => windowApi(db, id, capabilities),
});

export type SelectionApi = ReturnType<typeof createSelectionApi>;
